import type { ReactNode } from 'react';
import { Card } from './Card.js';
import { Icon, type IconName } from './Icon.js';

/**
 * A tela que não tem o que mostrar — e que **ensina** em vez de só constatar.
 *
 * ## O que estava errado, e não era a frase
 *
 * As frases já eram boas: "Nenhum boleto em seu nome nesta base", "Você ainda
 * não pediu nenhum cancelamento", cada uma seguida do que vai aparecer ali
 * quando houver algo e de como aquilo chega. É a regra da casa — vazio ensina a
 * tela — e ela estava cumprida.
 *
 * O que faltava era FORMA. Um cartão branco com um título em negrito e um
 * parágrafo, encostado no alto de uma tela de 844px, com 450px de nada embaixo:
 * a mesma anatomia de um cartão que carregou errado. Quem varre a tela em dois
 * segundos não lê o parágrafo — lê o formato, e o formato dizia "defeito".
 *
 * Aqui ele ganha um ícone num painel tingido, o título num degrau acima do de
 * cartão comum e o texto centrado numa medida curta. É a anatomia que o
 * `Evogard Portal v4` desenha na carteira vazia: um painel com ícone, e só
 * então a explicação.
 *
 * ## O ícone é MUDO, e é de propósito
 *
 * `aria-hidden` no `Icon` e nenhum `aria-label` no painel: quem usa leitor de
 * tela ouve o título e o parágrafo, que dizem tudo. Um nome acessível no
 * desenho faria a mesma informação ser anunciada duas vezes, a primeira delas
 * em forma de adivinhação ("ícone de carro").
 *
 * ## Continua sendo `Card`, e o título continua sendo cabeçalho
 *
 * Não é enfeite de acessibilidade: o estado vazio é uma REGIÃO da página com um
 * nome, e é por esse nome que alguém navegando por cabeçalhos chega até ele.
 * Trocá-lo por um `<p>` em negrito tiraria a tela do índice de quem não a
 * enxerga — e, de quebra, quebraria as buscas por `getByRole('heading')` que os
 * fluxos de navegador fazem.
 */
export function EmptyState({
  icon,
  title,
  children,
  actions,
}: {
  /** O desenho do assunto — o mesmo do item de menu que leva a esta tela. */
  icon: IconName;
  title: ReactNode;
  /** O que vai aparecer aqui quando houver, e como aquilo chega. */
  children: ReactNode;
  /** O caminho de saída, quando existe um. */
  actions?: ReactNode;
}) {
  return (
    <Card>
      <div className="flex flex-col items-center gap-4 px-2 py-6 text-center sm:py-8">
        <span className="grid size-14 shrink-0 place-items-center rounded-cartao bg-superficie-suave text-texto-suave">
          <Icon nome={icon} size={24} />
        </span>

        <div>
          <h2 className="text-base font-semibold">{title}</h2>
          {/*
           * `max-w-[46ch]` e não a largura do cartão: texto centrado só é
           * legível em linha curta, e no computador o cartão tem 920px. Sem o
           * teto, a explicação viraria uma linha de 120 caracteres com o olho
           * voltando ao começo errado a cada quebra.
           */}
          <p className="mx-auto mt-1.5 max-w-[46ch] text-sm text-texto-suave">{children}</p>
        </div>

        {actions}
      </div>
    </Card>
  );
}
